import { useEffect, useState } from "react";
import { motion, AnimatePresence, useReducedMotion } from "framer-motion";
import { Check, Zap, AlertTriangle, MessageSquare, Loader2 } from "lucide-react";

const CARDS = [
  { id: "RLY-4412", label: "Driver reassignment", meta: "Route 12", owner: "Maya K." },
  { id: "RLY-4415", label: "Vendor escalation", meta: "SLA risk", owner: "Devin R." },
  { id: "RLY-4418", label: "Shift handoff", meta: "Warehouse B", owner: "Priya S." },
  { id: "RLY-4423", label: "Invoice exception", meta: "3PL partner", owner: "Owen T." },
];

// state keys: unassigned | routed | progress | risk | done
const STATUS = {
  unassigned: { label: "Unassigned", cls: "text-ink-muted border-hairline bg-transparent", dot: "bg-ink-muted" },
  routed: { label: "Auto-Routed", cls: "text-signal border-signal/40 bg-signal/10", dot: "bg-signal" },
  progress: { label: "In Progress", cls: "text-ink border-ink/25 bg-white/5", dot: "bg-ink" },
  risk: { label: "At Risk", cls: "text-warning border-warning/50 bg-warning/10", dot: "bg-warning" },
  done: { label: "Done", cls: "text-signal border-signal/50 bg-signal/15", dot: "bg-signal" },
};

// self-playing timeline (~2.2s * 7 = ~15.4s loop)
const FRAMES = [
  { states: ["unassigned", "unassigned", "unassigned", "unassigned"], sla: 90, chat: null },
  { states: ["routed", "unassigned", "routed", "unassigned"], sla: 91, chat: null },
  { states: ["progress", "routed", "progress", "routed"], sla: 92, chat: "ping" },
  { states: ["progress", "risk", "progress", "progress"], sla: 89, chat: "answer" },
  { states: ["done", "progress", "progress", "progress"], sla: 93, chat: null },
  { states: ["done", "done", "done", "progress"], sla: 95, chat: null },
  { states: ["done", "done", "done", "done"], sla: 96, chat: null },
];

// static frame for reduced-motion — all information visible at once
const STATIC = { states: ["done", "risk", "progress", "routed"], sla: 94, chat: "answer" };

function StatusPill({ state }) {
  const s = STATUS[state];
  const Icon =
    state === "done" ? Check : state === "risk" ? AlertTriangle : state === "routed" ? Zap : state === "progress" ? Loader2 : null;
  return (
    <span
      className={`inline-flex items-center gap-1.5 rounded-full border px-2.5 py-1 font-mono text-[10.5px] font-medium tracking-wide ${s.cls}`}
    >
      {Icon ? (
        <Icon
          size={11}
          strokeWidth={2.5}
          className={state === "progress" ? "animate-spin [animation-duration:2.4s]" : ""}
        />
      ) : (
        <span className={`h-1.5 w-1.5 rounded-full ${s.dot}`} />
      )}
      {s.label}
    </span>
  );
}

function AnimatedSLA({ value, reduced }) {
  const [display, setDisplay] = useState(value);
  useEffect(() => {
    if (reduced) {
      setDisplay(value);
      return;
    }
    let raf;
    const start = display;
    const t0 = performance.now();
    const dur = 700;
    const tick = (now) => {
      const p = Math.min(1, (now - t0) / dur);
      const eased = 1 - Math.pow(1 - p, 3);
      setDisplay(Math.round(start + (value - start) * eased));
      if (p < 1) raf = requestAnimationFrame(tick);
    };
    raf = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(raf);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [value, reduced]);
  const tone = display >= 92 ? "text-signal" : display >= 90 ? "text-ink" : "text-warning";
  return <span className={`font-mono tabular-nums ${tone}`}>{display}%</span>;
}

export default function RoutingWidget() {
  const reduced = useReducedMotion();
  const [i, setI] = useState(0);

  useEffect(() => {
    if (reduced) return;
    const t = setInterval(() => setI((v) => (v + 1) % FRAMES.length), 2200);
    return () => clearInterval(t);
  }, [reduced]);

  const frame = reduced ? STATIC : FRAMES[i];

  return (
    <div
      data-testid="routing-widget"
      className="relative w-full rounded-2xl border border-hairline bg-bg-surface/90 p-4 shadow-[0_30px_80px_-30px_rgba(0,0,0,0.8)] backdrop-blur-sm sm:p-5"
    >
      {/* window chrome */}
      <div className="mb-4 flex items-center justify-between border-b border-hairline pb-3.5">
        <div className="flex items-center gap-2.5">
          <span className="relative flex h-2 w-2">
            {!reduced && (
              <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-signal opacity-60" />
            )}
            <span className="relative inline-flex h-2 w-2 rounded-full bg-signal" />
          </span>
          <span className="font-mono text-[11px] uppercase tracking-[0.2em] text-ink-muted">
            Live routing
          </span>
        </div>
        <div className="font-mono text-[11px] text-ink-muted">
          SLA compliance:{" "}
          <AnimatedSLA value={frame.sla} reduced={reduced} />
        </div>
      </div>

      {/* task cards */}
      <div className="space-y-2.5">
        {CARDS.map((card, idx) => {
          const state = frame.states[idx];
          const showOwner = state !== "unassigned";
          const chatOnThis = idx === 0 && frame.chat;
          return (
            <div
              key={card.id}
              className={`relative rounded-xl border px-3.5 py-3 transition-colors duration-500 ${
                state === "risk"
                  ? "border-warning/40 bg-warning/[0.04]"
                  : state === "done"
                  ? "border-signal/25 bg-signal/[0.04]"
                  : "border-hairline bg-bg-primary/40"
              }`}
            >
              <div className="flex items-center justify-between gap-3">
                <div className="min-w-0">
                  <div className="flex items-center gap-2">
                    <span className="whitespace-nowrap font-mono text-[10.5px] text-ink-muted">{card.id}</span>
                    <span className="h-1 w-1 shrink-0 rounded-full bg-ink-muted/50" />
                    <span className="truncate font-mono text-[10.5px] text-ink-muted">{card.meta}</span>
                  </div>
                  <div className="mt-1 truncate text-sm font-medium text-ink">{card.label}</div>
                </div>
                <div className="flex shrink-0 flex-col items-end gap-1.5">
                  <StatusPill state={state} />
                  <div className="h-4">
                    <AnimatePresence mode="wait">
                      {showOwner && (
                        <motion.div
                          key={state + card.owner}
                          initial={{ opacity: 0, x: 6 }}
                          animate={{ opacity: 1, x: 0 }}
                          exit={{ opacity: 0, x: -6 }}
                          transition={{ duration: 0.35 }}
                          className="flex items-center gap-1.5"
                        >
                          <span className="grid h-4 w-4 place-items-center rounded-full bg-signal/20 font-mono text-[8px] font-semibold text-signal">
                            {card.owner.split(" ").map((n) => n[0]).join("")}
                          </span>
                          <span className="font-mono text-[10px] text-ink-muted">{card.owner}</span>
                        </motion.div>
                      )}
                    </AnimatePresence>
                  </div>
                </div>
              </div>

              {/* chat beat on card 0 — the AI Communication Agent made visible */}
              <AnimatePresence>
                {chatOnThis && (
                  <motion.div
                    key={frame.chat}
                    initial={{ opacity: 0, height: 0, marginTop: 0 }}
                    animate={{ opacity: 1, height: "auto", marginTop: 10 }}
                    exit={{ opacity: 0, height: 0, marginTop: 0 }}
                    transition={{ duration: 0.4, ease: [0.22, 1, 0.36, 1] }}
                    className="overflow-hidden"
                  >
                    <div className="space-y-1.5 border-t border-hairline pt-2.5">
                      <div className="flex items-center gap-2">
                        <MessageSquare size={11} className="text-ink-muted" />
                        <span className="rounded-md rounded-tl-none bg-white/5 px-2 py-1 font-mono text-[10.5px] text-ink-muted">
                          status?
                        </span>
                      </div>
                      {frame.chat === "answer" && (
                        <motion.div
                          initial={{ opacity: 0, y: 4 }}
                          animate={{ opacity: 1, y: 0 }}
                          transition={{ delay: 0.25, duration: 0.35 }}
                          className="flex items-center justify-end gap-2"
                        >
                          <span className="max-w-[85%] rounded-md rounded-tr-none bg-signal/15 px-2 py-1 text-right font-mono text-[10.5px] text-signal">
                            Owned by Maya K. · ETA 14m · on track
                          </span>
                          <span className="grid h-4 w-4 shrink-0 place-items-center rounded-full bg-signal font-mono text-[8px] font-bold text-bg-primary">
                            AI
                          </span>
                        </motion.div>
                      )}
                    </div>
                  </motion.div>
                )}
              </AnimatePresence>
            </div>
          );
        })}
      </div>

      {/* footer readout */}
      <div className="mt-4 flex items-center justify-between border-t border-hairline pt-3 font-mono text-[10px] text-ink-muted">
        <span>relayops · auto-triage engine</span>
        <span className="flex items-center gap-1.5">
          <span className={`h-1.5 w-1.5 rounded-full bg-signal ${reduced ? "" : "animate-pulse-dot"}`} />
          {reduced ? "paused" : "streaming"}
        </span>
      </div>
    </div>
  );
}
